import { atomWithStorage } from 'jotai/utils';

import {
  AccountType,
  mapAccountTypeToSubscriptionType,
} from '@/components/Modals/CreateAccount/types';
import { type Plan, plans } from '@/fixtures/plans';
import { createCookieValueStorage } from '@/utils/cookie-storage';

export const SUBSCRIPTION_TYPE_COOKIE_NAME = 'uppbeat_subscription_type';

/**
 * Type guard to check if a string is a valid subscription type.
 */
export function isSubscriptionType(value: unknown): value is Plan['name'] {
  return (
    typeof value === 'string' &&
    plans.map((plan) => plan.name).includes(value as Plan['name'])
  );
}

export function isAccountType(value: unknown): value is AccountType {
  return (
    typeof value === 'string' &&
    Object.values(AccountType).includes(value as AccountType)
  );
}

// Create cookie storage instance using the utility
const subscriptionTypeCookieStorage = createCookieValueStorage<
  Plan['name'] | undefined
>(isSubscriptionType, undefined, {
  expires: 30, // 30 days
});

/**
 * Store the selected subscription type in cookies
 */
export const selectedSubscriptionTypeAtom = atomWithStorage<
  Plan['name'] | undefined
>(SUBSCRIPTION_TYPE_COOKIE_NAME, undefined, subscriptionTypeCookieStorage);

selectedSubscriptionTypeAtom.debugLabel = 'selectedSubscriptionTypeAtom';

/**
 * Parse the subscription type from the query params
 * Simple parser for query state serialization
 */
export const parseSubscriptionType = {
  parse: (value: string | null): Plan['name'] | null => {
    if (!value) {
      return null;
    }

    if (isSubscriptionType(value)) {
      return value;
    }

    if (isAccountType(value)) {
      return mapAccountTypeToSubscriptionType[value];
    }

    return null;
  },
  serialize: (value: Plan['name'] | undefined): string => value ?? '',
};
